import Button from "@components/ui/Button";
import Skeleton from "@components/scheduling/analytics/Skeleton";
import { formatWireTime } from "@components/scheduling/campaignLabels";

const REASONS = {
  disabled: "Summaries are switched off for this clinic",
};
const DEFAULT_REASON = "Could not write a summary right now";

function Frame({ children, action }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between gap-4">
        <h3 className="text-sm font-medium text-gray-900">Campaign summary</h3>
        {action}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

/**
 * The written summary. It has its own four states so that a slow or failed
 * model call never touches the numbers above it: writing (skeleton lines),
 * error (the server's message, verbatim, with Retry), unavailable (why, with
 * Retry), and ready (the text, when it was written, Regenerate).
 */
export default function InsightCard({
  insight, loading = false, error = null, regenerating = false, onRetry, onRegenerate,
}) {
  if (loading) {
    return (
      <Frame>
        <div className="space-y-2" aria-busy="true">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-11/12" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <p className="mt-3 text-xs text-gray-500">Writing your summary…</p>
      </Frame>
    );
  }

  const retry = onRetry && (
    <Button color="light" size="sm" onClick={onRetry}>Retry</Button>
  );

  if (error) {
    return (
      <Frame action={retry}>
        <p role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      </Frame>
    );
  }

  if (!insight || insight.status !== "ready") {
    const reason = insight?.reason;
    return (
      <Frame action={retry}>
        <p className="text-sm font-medium text-gray-700">Summary unavailable</p>
        <p className="mt-1 text-sm text-gray-500">{REASONS[reason] || DEFAULT_REASON}</p>
      </Frame>
    );
  }

  const regenerate = onRegenerate && (
    <Button color="light" size="sm" onClick={onRegenerate} disabled={regenerating}>
      {regenerating ? "Regenerating…" : "Regenerate"}
    </Button>
  );

  return (
    <Frame action={regenerate}>
      <p className="whitespace-pre-line text-base leading-relaxed text-gray-800">{insight.text}</p>
      {insight.generated_at && (
        <p className="mt-3 text-xs text-gray-500">Generated {formatWireTime(insight.generated_at)}</p>
      )}
    </Frame>
  );
}
